import { defineStore } from "pinia";
import { computed, reactive, toRefs, watch } from "vue";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { IDomainMessagingChannel } from "~/domain/messaging-channel";
import type { IDomainColorSet } from "~/domain/colors-set";
import type { IDomainMenuDisplayLayout } from "~/domain/menu-display-layout";
import {
  useAnalytics,
  useBugReporter,
  useFeatureFlag,
  useLayoutManager,
} from "#imports";
import { SalesChannel } from "~/domain/sales-channels";
import { DEFAULT_LOCALE } from "~/domain/constants";
import type { IDomainStyles } from "~/domain/styles";
import type { IDomainSiteLink, IDomainSiteLinkType } from "~/domain/site-link";

const TERMS_AND_CONDITIONS_LINK_KEY = "terms_conditions";
const PRIVACY_POLICY_LINK_KEY = "privacy_policy";
const PRIVACY_COOKIE_POLICY_LINK_KEY = "privacy_cookie_policy";

// Define the state interface
type IState = {
  isLoaded: boolean;
  id: string;
  name: string;
  slug: string;
  country: string;
  currency: string;
  locale: string;
  languages: string[];
  hasPickup: boolean;
  hasDelivery: boolean;
  defaultOrderingMode: IDomainOrderingMode;
  defaultMessagingChannel: IDomainMessagingChannel;
  menuDisplayLayout: IDomainMenuDisplayLayout;
  logo?: string;
  cover?: string;
  whatsapp?: number;
  colorSet?: IDomainColorSet;
  designTokens: Record<string, string>;
  styles?: IDomainStyles;
  gtmContainerId?: string;
  salesChannel: SalesChannel;
  config: {
    isTipsEnabled: boolean;
    acceptsDriverTips: boolean;
    tipPercentages: number[];
    isPostalCodeRequired: boolean;
  };
  siteLinks: Record<IDomainSiteLinkType, IDomainSiteLink[]>;
};

// Helper function to create the initial state
function createInitialState(): IState {
  return {
    isLoaded: false,
    id: "",
    name: "",
    slug: "",
    country: "",
    currency: "",
    locale: DEFAULT_LOCALE,
    languages: [],
    logo: undefined,
    cover: undefined,
    whatsapp: undefined,
    hasPickup: true,
    hasDelivery: true,
    defaultOrderingMode: "delivery",
    defaultMessagingChannel: "whatsapp",
    menuDisplayLayout: "grid",
    colorSet: undefined,
    designTokens: {},
    styles: undefined,
    gtmContainerId: undefined,
    salesChannel: SalesChannel.WEB_ORDERING,
    config: {
      isTipsEnabled: true,
      acceptsDriverTips: false,
      tipPercentages: [5, 10, 15],
      isPostalCodeRequired: false,
    },
    siteLinks: {
      social: [],
      legal: [],
    },
  };
}

export const useBusinessStore = defineStore("business", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const isMenuGrid = computed(() => state.menuDisplayLayout === "grid");

  const isMobileApp = computed(
    () => state.salesChannel === SalesChannel.MOBILE_APP,
  );

  const isCatering = computed(
    () => state.salesChannel === SalesChannel.CATERING,
  );

  const termsAndConditionsLink = computed(() => {
    return state.siteLinks.legal.find(
      (siteLink) => siteLink.slug === TERMS_AND_CONDITIONS_LINK_KEY,
    )?.url;
  });

  const privacyPolicyLink = computed(() => {
    return state.siteLinks.legal.find(
      (siteLink) => siteLink.slug === PRIVACY_POLICY_LINK_KEY,
    )?.url;
  });

  const privacyPolicyCookieLink = computed(() => {
    return state.siteLinks.legal.find(
      (siteLink) => siteLink.slug === PRIVACY_COOKIE_POLICY_LINK_KEY,
    )?.url;
  });

  const { reportGlobalTags } = useBugReporter();
  const { setContextField, loadBasicFlags } = useFeatureFlag();
  const { setSuperProperties } = useAnalytics();
  const { setActiveSalesChannel } = useLayoutManager();

  watch(
    () => state.isLoaded,
    async () => {
      reportGlobalTags({
        "business.id": state.id || undefined,
        "business.slug": state.slug || undefined,
      });

      setSuperProperties({
        businessId: state.id || undefined,
        businessName: state.name || undefined,
        businessSlug: state.slug || undefined,
        salesChannel: state.salesChannel || undefined,
      });

      setContextField("businessId", state.id || undefined);
      setContextField("businessSlug", state.slug || undefined);
      setContextField("businessCountry", state.country || undefined);
      await loadBasicFlags();
    },
  );

  watch(
    () => state.salesChannel,
    (value) => setActiveSalesChannel(value),
  );

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    isMenuGrid,
    isMobileApp,
    isCatering,
    privacyPolicyLink,
    privacyPolicyCookieLink,
    termsAndConditionsLink,
  };
});
